<template>
  <!-- First-paint splash: shown only while hydrating an auth-required route.
       Public routes (watch-party, locked, unlock-error) bypass the splash
       entirely and render immediately. -->
  <div
    v-if="showSplash"
    class="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900"
  >
    <div class="text-center">
      <div class="inline-block w-8 h-8 border-2 border-gray-300 dark:border-gray-700 border-t-blue-600 rounded-full animate-spin" role="status" aria-label="Loading"></div>
      <p class="mt-3 text-sm text-gray-500 dark:text-gray-500">Loading…</p>
    </div>
  </div>

  <div v-else class="app-root" :class="{ 'collection-app': showHeader }" :inert="titlePreviewOpen || undefined">
    <!-- TV-mode auto-detect indicator. Used to be a full-width blue
         banner; now collapsed to a discreet bottom-right pill that
         doesn't compete with the cinematic chrome. Same affordances
         (switch-to-desktop, dismiss) on hover/click. -->
    <div
      v-if="uiStore.tvAutoDetected"
      class="tv-auto-pill"
      role="status"
      aria-label="TV mode auto-enabled"
    >
      <span class="tv-auto-pill__label">TV mode</span>
      <button
        type="button"
        class="tv-auto-pill__action"
        @click="switchToDesktop"
        title="Switch to desktop mode"
      >
        Desktop
      </button>
      <button
        type="button"
        class="tv-auto-pill__close"
        aria-label="Dismiss"
        @click="dismissBanner"
      >×</button>
    </div>

    <!-- Cinematic slim header — used in place of the standard chrome
         whenever TV mode is on. Hides on public routes (/locked,
         /watch-party, /remote, etc.) just like the standard header. -->
    <TvSlimHeader v-if="showSlimHeader" @voice-search="openVoiceSearch" @search="openHeaderSearch" />

    <CollectionShell v-if="showHeader" @search="openHeaderSearch" />

    <div :class="{ 'collection-workspace': showHeader }">
    <!-- Feature 42 — surface playback happening on another device on the
         same invite. Renders nothing when there's no foreign session. -->
    <!-- Full handoff pill: desktop homepage only. Elsewhere — and in TV mode,
         where it was unreachable by D-pad and covered content — the reachable
         header HandoffIndicator takes over (#330). -->
    <HandoffBanner v-if="authStore.isUnlocked && !uiStore.tvMode && ['library-all', 'discover', 'live-tv'].includes(route.name)" />

    <!-- Homepage preferences prompt (replaces the removed OnboardingWizard).
         A slim, non-blocking card nudging new users to set language + region
         until they do. Only on the desktop/phone home routes; TV users set
         these in Settings. -->
    <div v-if="showPrefsPrompt" class="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 pt-4">
      <PreferencesPrompt />
    </div>

    <!-- Main content layout
         - Cinematic routes (/tv): zero padding, edge-to-edge.
         - TV mode on other routes: light padding (px-16 pt-20) so content
           sits below the slim 56px header and the page has room to breathe
           without going full-width.
         - Desktop: standard container/padding as before.
    -->
    <main id="main-content" :class="mainWrapperClass" tabindex="-1">
      <!-- ONE KeepAlive around every route, matching by component name.
           The previous v-if/v-else split unmounted the KeepAlive itself the
           moment a NON-cached route (a /browse page, a player) rendered, so
           its cache only ever survived tab switches between cached routes —
           Back from a title page always re-mounted the whole home page
           (measured on the Chromecast: ~1.3 s of main-thread work plus ~3 s
           of 100–300 ms frames while 11 rails re-fetched and re-rendered).
           Non-cached routes are untouched by `include`; cached ones keep
           their DOM, scroll offsets and fetched rails, so Back is a
           re-attach. -->
      <RouterView v-slot="{ Component, route: viewRoute }">
        <KeepAlive :include="KEEP_ALIVE_COMPONENTS" :max="6">
          <component
            :is="Component"
            :key="routeCacheKey(viewRoute)"
          />
        </KeepAlive>
      </RouterView>

    </main>
    </div>

    <!-- Voice Search Overlay (TV-mode mic button) -->
    <VoiceSearchOverlay
      v-if="showVoiceOverlay"
      :voice="voice"
      @done="onVoiceDone"
      @fallback="onVoiceFallback"
    />

    <!-- Header search overlay — fixed-position, fades over the header when
         the magnifier is clicked from either chrome (desktop or TV slim).
         Submit (Enter) routes to /discover?q=<text>. Esc / X close. -->
    <HeaderSearchOverlay v-model="showHeaderSearch" />

    <!-- Phone-as-remote trackpad cursor. Stays invisible until the paired
         phone sends cursor moves; auto-hides after a couple of seconds of
         inactivity. Listens to window events dispatched by
         useRemoteControl so it stays independent of the WS layer. -->
    <RemoteCursor />

    <!-- App-wide toast host. Renders the stack from useToast(); any
         component / store can call `useToast().success(...)` etc. -->
    <ToastHost />
    <TitlePreviewHost />

    <!-- Global keyboard shortcuts overlay. Bound to "?" anywhere on
         the page (skipped when typing into a field). v-if so onMounted
         (and the focus trap) fire fresh every time the overlay opens. -->
    <KeyboardShortcutsOverlay
      v-if="showShortcuts"
      @close="showShortcuts = false"
    />

  </div>
</template>

<script setup>
import { KeepAlive, computed, defineAsyncComponent, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { RouterView, useRoute, useRouter } from 'vue-router'
import TitlePreviewHost from '@/components/TitlePreviewHost.vue'
import { canUseTitlePreviewRoute, parseTitlePreview, TITLE_PREVIEW_QUERY } from '@/helpers/title-preview'
import { usePutioStore } from '@/stores/putio'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'

import CollectionShell from '@/components/CollectionShell.vue'
import HeaderSearchOverlay from '@/components/HeaderSearchOverlay.vue'
import ToastHost from '@/components/ToastHost.vue'
import HandoffBanner from '@/components/HandoffBanner.vue'
import RemoteCursor from '@/components/RemoteCursor.vue'

// TV-mode-only chrome: v-if-gated on showSlimHeader (which requires tvMode),
// so desktop sessions never fetch the chunk. position:fixed, so the late
// async mount can't shift layout. (RemoteCursor stays static — it mounts
// unconditionally and only v-shows itself, so async-loading it would just
// add an extra always-fetched chunk.)
const TvSlimHeader = defineAsyncComponent(() => import('@/components/TvSlimHeader.vue'))

// Rarely-shown, v-if-gated overlays: load on demand so their code (and
// deps like the voice-search wrapper / focus trap) stays out of the eager
// entry chunk. They only ever render on an explicit user action — the TV
// mic button (VoiceSearchOverlay) or pressing "?" (KeyboardShortcutsOverlay).
const VoiceSearchOverlay = defineAsyncComponent(() => import('@/components/VoiceSearchOverlay.vue'))
const KeyboardShortcutsOverlay = defineAsyncComponent(() => import('@/components/KeyboardShortcutsOverlay.vue'))
// Homepage preferences prompt — only renders for un-onboarded users, so it
// loads on demand rather than weighing down first paint for everyone. Gated by
// v-if="showPrefsPrompt".
const PreferencesPrompt = defineAsyncComponent(() => import('@/components/PreferencesPrompt.vue'))
import { isLikelyTvDevice, isRemoteNavPreferred } from '@/helpers/remote-nav'
import { useSettingsStore } from '@/stores/settings'
import { useTasteStore } from '@/stores/taste'
import { hydrate as hydratePlayback } from '@/services/playbackPosition'
import { hydrate as hydrateSeriesProgress } from '@/services/seriesProgress'
import { useVoiceSearch } from '@/composables/useVoiceSearch'
import { useRemoteControl } from '@/composables/useRemoteControl'
import { useAppUpdate } from '@/composables/useAppUpdate'
import { useRoutePrefetch } from '@/composables/useRoutePrefetch'

const putio = usePutioStore()
const authStore = useAuthStore()
const uiStore = useUiStore()
const settingsStore = useSettingsStore()
const route = useRoute()
const router = useRouter()
// Warm code-split route chunks on link hover/focus so navigation feels instant
// (hover-capable, non-data-saver devices only — see the composable).
const routePrefetch = useRoutePrefetch(router)
// Spatial-nav (TV D-pad navigation) is a large, TV-only module. Load it on
// demand the first time remote/TV navigation turns on so desktop sessions
// never pay for it. useSpatialNav() has no Vue lifecycle hooks, so lazy
// instantiation outside setup() is safe.
let spatialNav = null
let spatialNavLoading = null
function ensureSpatialNav() {
  if (spatialNav) return Promise.resolve(spatialNav)
  if (!spatialNavLoading) {
    spatialNavLoading = import('@/composables/useSpatialNav').then((m) => {
      spatialNav = m.useSpatialNav()
      return spatialNav
    })
  }
  return spatialNavLoading
}
// Keep long-lived TV/PWA sessions from getting stuck on an old build: poll for
// new deploys and reload at the next non-player navigation.
useAppUpdate(router)
const voice = useVoiceSearch()
// Singleton TV-side remote-control session. We auto-start it whenever this
// device is acting as the TV (see the tvMode watcher below) so a phone on
// the same invite can connect as a remote straight from its header icon —
// no QR scan needed.
const remote = useRemoteControl()
const titlePreviewOpen = computed(() => authStore.canBrowseTorrents && !uiStore.tvMode && canUseTitlePreviewRoute(route) && !!parseTitlePreview(route.query[TITLE_PREVIEW_QUERY]))
const showVoiceOverlay = ref(false)
const showShortcuts = ref(false)
const remoteNavEnabled = computed(() => isRemoteNavPreferred(uiStore))

// "?" anywhere on the page toggles the shortcuts overlay. Skipped when the
// active element is an editable field — typing "?" in a search input should
// produce a literal "?" instead of stealing focus. Capture-phase listener so
// it wins over spatial-nav's keydown handler.
function handleGlobalShortcutKey(event) {
  if (event.defaultPrevented || titlePreviewOpen.value) return
  if (event.key !== '?' && event.key !== '/') return
  const active = document.activeElement
  if (active && (
    active.tagName === 'INPUT' ||
    active.tagName === 'TEXTAREA' ||
    active.tagName === 'SELECT' ||
    active.isContentEditable
  )) return
  event.preventDefault()
  if (event.key === '/') openHeaderSearch()
  else showShortcuts.value = !showShortcuts.value
}

watch(
  remoteNavEnabled,
  async (on) => {
    if (on) {
      const nav = await ensureSpatialNav()
      // Guard against a fast toggle back off while the module was loading.
      if (remoteNavEnabled.value) nav.install()
    } else {
      spatialNav?.uninstall()
    }
  },
  { immediate: true },
)

// Auto-broadcast a remote-control session whenever this device is acting as
// the TV (TV mode on, unlocked, and the invite carries the remote-control
// permission). This is what makes the phone's header remote icon light up
// without anyone opening the QR pairing modal on the TV first: the session
// exists the moment the TV is in use, /api/remote/active starts returning
// it for the household, and the phone can one-tap connect.
//
// start() is idempotent (it no-ops when a session is already live or
// reconnecting), so this composes cleanly with the explicit "Pair remote"
// modal — opening that modal just reveals the QR for the session created
// here. We deliberately DON'T stop the session when TV mode flips off: it
// idle-expires server-side once both sockets drop, and tearing it down on a
// transient toggle could kill a remote a phone is actively using.
watch(
  () =>
    uiStore.tvMode &&
    authStore.isUnlocked &&
    authStore.canPairRemote,
  (shouldBroadcast) => {
    if (shouldBroadcast) remote.start()
  },
  { immediate: true },
)

// Close the header search overlay on route change. The submit handler
// already routes to /discover, so leaving the overlay open afterwards would
// hover over the new page's header.
watch(
  () => route.fullPath,
  () => {
    showHeaderSearch.value = false
  },
)

// Hide header on public routes (locked, unlock-error, watch-party) and when
// the user isn't authenticated. Router guard is the authority on access;
// this only controls chrome visibility.
const showHeader = computed(() => {
  if (route.meta.public) return false
  // TV mode replaces the standard chrome with TvSlimHeader so we don't
  // double up. Public routes hide both.
  if (uiStore.tvMode) return false
  return authStore.status === 'unlocked' || authStore.status === 'admin'
})

const showSlimHeader = computed(() => {
  if (route.meta.public) return false
  if (!uiStore.tvMode) return false
  // Full-screen player routes (Live TV player, future video views)
  // own the full viewport with their own top-bar chrome. Showing the
  // app's slim header on top would stack two fixed bars at z-40/z-50
  // and visibly overlap the channel/back controls.
  if (route.name === 'live-tv-player') return false
  // Routes that conditionally swap between windowed and cinematic
  // (MediaView in TV mode VIDEO branch, TorboxPlayView in TV mode)
  // flip cinematicPlayerActive on mount of their cinematic shell so
  // we can hide the slim header just for that state, not the whole
  // route. The shell unmount restores it on the FOLDER / windowed
  // branch.
  if (uiStore.cinematicPlayerActive) return false
  return authStore.status === 'unlocked' || authStore.status === 'admin'
})

// Belt-and-suspenders: clear cinematicPlayerActive on every route
// change. View-level onUnmounted hooks should handle this, but
// HMR + router-view re-render order can leave the flag stuck when
// the user navigates away from a cinematic-mounted view. Clearing
// on route name change means the next view's onMounted/watcher
// always starts from false and sets it true only if it actually
// renders a cinematic shell.
watch(
  () => route.name,
  () => {
    if (uiStore.cinematicPlayerActive) uiStore.setCinematicPlayerActive(false)
  },
)

const mainWrapperClass = computed(() => {
  if (route.meta.cinematic) return '' // /tv → fully edge-to-edge
  // Public auth-gate pages (/locked, /unlock-error, /unlock-devices/:token)
  // render a self-contained min-h-screen centered card. Wrapping them in
  // tv-main-padding adds 80px+64px of vertical chrome on top of an already
  // 100vh inner element, which overflows the viewport. They also don't
  // surface any TV focusable chrome that needs the cinematic gutters, so
  // fall through to the no-padding wrapper regardless of TV mode.
  if (route.meta.public) return ''
  if (uiStore.tvMode) return ['tv-main-padding', route.name === 'library-all' && 'tv-collection-main']
  return 'collection-main'
})

// Show the first-paint splash only while hydrating an auth-required route.
// Public routes render immediately — important for watch-party (anonymous
// viewers) and the locked page itself.
const showSplash = computed(
  () => authStore.status === 'loading' && !route.meta.public,
)

const showHeaderSearch = ref(false)

// Homepage preferences prompt. Replaces the old multi-step OnboardingWizard:
// a slim non-blocking card that nudges un-onboarded users to set language +
// region, shown on the desktop/phone home routes until they save (which flips
// the server `onboarded` flag). Requires the `discover` permission since region
// /language only affect Discover. Admins are auto-onboarded so they don't see
// it; locked routes never trigger it. TV mode is excluded — TV users set these
// in Settings rather than via a top-of-page banner over the cinematic layout.
const showPrefsPrompt = computed(() => {
  if (uiStore.tvMode) return false
  if (authStore.status !== 'unlocked' && authStore.status !== 'admin') return false
  if (authStore.onboarded !== false) return false
  if (!(authStore.hasPermission?.('discover'))) return false
  return route.name === 'home' || route.name === 'discover'
})

const openHeaderSearch = () => {
  const libraryRoute = ['library-all', 'library', 'sources-plex-browse'].includes(route.name)
  const localSearch = libraryRoute ? document.querySelector('main input[type="search"]') : null
  if (localSearch) {
    const disclosure = localSearch.closest('details')
    if (disclosure) disclosure.open = true
    localSearch.focus()
    localSearch.scrollIntoView({ block: 'center', behavior: 'instant' })
    return
  }
  if (authStore.canBrowseTorrents) showHeaderSearch.value = true
}

// Let any child view request the header search overlay (e.g. DiscoverView's
// active-search chip → "edit my query"). uiStore.openSearchOverlay() bumps
// searchOverlayTick; we open the same overlay the magnifier uses, which
// prefills from ?q= and parks the caret at the end for editing.
watch(
  () => uiStore.searchOverlayTick,
  () => { openHeaderSearch() },
)

const switchToDesktop = () => {
  uiStore.setTvMode(false)
  uiStore.dismissAutoDetect()
}

const dismissBanner = () => {
  uiStore.dismissAutoDetect()
}

// Component names (SFC filenames — no view sets an explicit `name`) the
// RouterView's KeepAlive retains. Everything else mounts/unmounts as usual.
const KEEP_ALIVE_COMPONENTS = [
  // '/' — the home / Discover hub. Kept alive so Back from a title page or a
  // player re-attaches the rails instead of re-mounting them (TV audit
  // 2026-09-05). DiscoverView is fully query-reactive, so one instance serves
  // every ?q/?genre/... state — the key deliberately ignores the query.
  'DiscoverView', 'TvHomeView',
  'LibraryBrowseView',
  'LibraryAllView',
  'PlexBrowseView',
]

const CACHED_LIBRARY_ROUTE_NAMES = new Set([
  'discover', 'discover-movies', 'discover-tv', 'tv',
  'library',
  'library-all',
  'sources-plex-browse',
])

function shouldCacheLibraryRoute(viewRoute) {
  return CACHED_LIBRARY_ROUTE_NAMES.has(String(viewRoute?.name || ''))
}

// Cache key for the routed component. Cached routes get a stable key so
// KeepAlive can find the instance again; everything else returns undefined
// (= no key, the RouterView default: same component across param changes is
// reused and watches route.params itself).
function routeCacheKey(viewRoute) {
  if (!shouldCacheLibraryRoute(viewRoute)) return undefined
  return libraryRouteCacheKey(viewRoute)
}

function libraryRouteCacheKey(viewRoute) {
  const name = String(viewRoute?.name || '')
  if (name === 'tv' || name.startsWith('discover')) return name
  if (name === 'library') {

    return [
      name,
      String(viewRoute?.query?.section || ''),
      String(viewRoute?.query?.view || ''),
    ].join(':')
  }
  if (name === 'library-all') {
    // NOTE: `section` is deliberately NOT part of the key (Feature 43).
    // Switching the All / Movies / TV tabs (?section=movies → ?section=tv) is
    // the SAME merged pool — keying on section would remount the view and
    // refire the slow Plex `recently-added` fan-out on every tab switch. The
    // section is fully reactive (the `activeSection` computed + every
    // section-derived shelf/tab/grid), so one cached instance handles all
    // tabs and only refreshes in the background. Mirrors sources-plex-browse.
    return name
  }
  if (name === 'sources-plex-browse') {
    // NOTE: `section` is deliberately NOT part of the key. Switching section
    // tabs (?section=4 → ?section=6) is the SAME server — keying on section
    // would remount the whole view, refetch the libraries list and reset the
    // header title to the "Plex" placeholder (serverName is seeded from the
    // query, which the tab links don't carry). The section is fully reactive
    // (inSectionMode / activeSectionLibrary computeds + per-section shelf
    // keys), so the instance can persist and update in the background.
    return [
      name,
      String(viewRoute?.params?.accountId || ''),
      String(viewRoute?.params?.serverId || ''),
      String(viewRoute?.query?.show || ''),
    ].join(':')
  }
  return viewRoute?.fullPath || name
}

const openVoiceSearch = () => {
  if (!voice.supported.value) return
  showVoiceOverlay.value = true
  // Pass the user's preferred TMDB language as the speech locale. The Web
  // Speech API expects BCP-47 tags ("en-US", "fr-FR"); language tags from
  // settings are already in that shape.
  voice.start(settingsStore.language || 'en-US')
}

const onVoiceDone = (finalTranscript) => {
  showVoiceOverlay.value = false
  const trimmed = (finalTranscript || '').trim()
  if (!trimmed) return
  // Route to wherever the user is allowed to search. /discover is the
  // ideal target (it has a TMDB-backed search + filter bar), but it
  // requires the discover permission. Library-only users get sent to
  // /library?q=... so voice search still does something useful for
  // them. Live-tv-only users get /live-tv/browse?q=... -- those views
  // already accept a `q` query param.
  let target
  if (authStore.canBrowseTorrents) target = '/discover'
  else if (authStore.canBrowseLiveTv) target = '/live-tv/browse'
  else target = '/library'
  router.push({ path: target, query: { q: trimmed } })
}

// "Type instead" escape hatch. When the Web Speech API errors out
// (common on Android TV Chrome with 'service-not-allowed' / 'audio-capture'),
// the overlay closes and we drop the user on the search surface with the
// header search overlay opened so they can type their query.
const onVoiceFallback = () => {
  showVoiceOverlay.value = false
  let target
  if (authStore.canBrowseTorrents) target = '/discover'
  else if (authStore.canBrowseLiveTv) target = '/live-tv/browse'
  else target = '/library'
  router.push({ path: target })
  // Open the search overlay on the next tick so the route has time to
  // mount before the overlay tries to focus its input.
  nextTick(() => {
    showHeaderSearch.value = true
  })
}

// Capture the PWA install prompt at the App level so any view (Settings, an
// Install button, etc.) can fire it later via uiStore.triggerInstallPrompt().
// Browsers fire `beforeinstallprompt` exactly once per page load, so we hold
// onto the event until it's consumed or the page unloads.
const handleBeforeInstallPrompt = (e) => {
  e.preventDefault()
  uiStore.setInstallPromptEvent(e)
}
const handleAppInstalled = () => {
  // Once installed there's nothing left to prompt.
  uiStore.setInstallPromptEvent(null)
}

// Custom event from useRemoteControl — phone's voice button fires it via
// window.dispatchEvent(new CustomEvent('remote-voice-search')). We treat
// it as a synthetic mic-button tap so the existing overlay opens with
// the same locale and same teardown behavior.
const handleRemoteVoiceSearch = () => {
  if (voice.supported.value) openVoiceSearch()
}

onMounted(async () => {
  // Re-apply the .tv-mode class from persisted state. Pinia's persistence
  // plugin restores `tvMode` to the store on hydration, but the class on
  // <html> is DOM state — `setTvMode` is what writes it, and persistence
  // doesn't replay actions. Without this call, the page boots with
  // tvMode=true in the store but no .tv-mode class until the user toggles.
  uiStore.applyTvModeClass()

  document.addEventListener('keydown', handleGlobalShortcutKey)
  window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
  window.addEventListener('appinstalled', handleAppInstalled)
  window.addEventListener('remote-voice-search', handleRemoteVoiceSearch)
  routePrefetch.install()
  // Warm the auth cache at boot. The router guard awaits its own hydrate()
  // call — this is a fire-and-forget that usually completes before the
  // guard needs it, flipping showSplash off quickly.
  //
  // We also gate the Put.io client init on the hydrate result: calling
  // /api/putio/account/info before the user is unlocked produces a 401
  // that the proxy can't avoid, which Lighthouse counts under BP's
  // `errors-in-console`. By waiting for hydrate, we skip the call entirely
  // when state is `locked` (the router will already be redirecting).
  authStore.hydrate().then(async () => {
    const authed =
      authStore.status === 'unlocked' || authStore.status === 'admin'
    if (authed) {
      // Per-invite preferences (region, language, …) live on the server
      // and are shared across every device on the same invite. Hydrate
      // them once auth confirms we have an invite — overrides any
      // localStorage guess so a fresh device picks up what the user
      // already configured elsewhere.
      settingsStore.hydratePreferencesFromServer()

      // Continue Watching state is server-backed (per invite, not per
      // device) so playback resumes seamlessly across phone/desktop.
      // Fire-and-forget — the rail components also call hydrate() so
      // a slow boot doesn't leave them empty, but doing it here too
      // warms the cache before any /media page can request a position.
      hydratePlayback().catch(() => { /* nbd, retries on next boot */ })

      // Series "Continue Watching" progress is server-backed per invite too
      // (used to be device-local localStorage, which never synced phone↔laptop
      // for the same user). Warm the cache so the rail is identical everywhere.
      hydrateSeriesProgress().catch(() => { /* nbd, retries on next boot */ })

      // Taste signals (Feature 73): ratings + watched marks, server-backed per
      // invite. Warm them here so the /browse hero buttons and the
      // exclusion-filtered rails read a hydrated store. Re-entrant + never
      // throws — components that need it awaited call load() themselves.
      useTasteStore().load()

      try {
        if (!putio.client) {
          await putio.initializeClient()
        }
      } catch (error) {
        // Auth was good a moment ago but the API call still failed —
        // log so it's debuggable. 401/403 here would be a race (auth
        // expired between hydrate and the next call), handled elsewhere
        // by apiClient's redirect; downgrade to warn either way.
        const status = error?.response?.status
        if (status !== 401 && status !== 403) {
          console.warn('Put.io initialization failed:', error)
        }
      }
    }
  })

  if (
    !uiStore.tvAutoDetected &&
    !uiStore.tvMode &&
    isLikelyTvDevice()
  ) {
    uiStore.setTvMode(true)
    uiStore.setTvAutoDetected(true)
    if (route.name !== 'tv') {
      router.replace('/tv')
    }
  }
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleGlobalShortcutKey)
  window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
  window.removeEventListener('appinstalled', handleAppInstalled)
  window.removeEventListener('remote-voice-search', handleRemoteVoiceSearch)
  routePrefetch.uninstall()
  spatialNav?.uninstall()
})
</script>

<style scoped>
/* TV-mode auto-detect pill -- small bottom-right indicator instead of
   the previous full-width blue banner. The banner was loud and
   permanently occupied page-top real estate. Pill is dismissable and
   carries the same "Switch to desktop" affordance. */
.tv-auto-pill {
  position: fixed;
  bottom: 14px;
  left: 14px;
  z-index: 45;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px;
  border-radius: 9999px;
  background: rgba(15, 23, 42, 0.55);
  color: rgba(226, 232, 240, 0.92);
  border: 1px solid rgba(148, 163, 184, 0.25);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.02em;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}
.tv-auto-pill__label {
  opacity: 0.75;
}
.tv-auto-pill__action {
  color: rgba(147, 197, 253, 0.95);
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
  background: transparent;
  border: 0;
  padding: 0;
  font-size: inherit;
}
.tv-auto-pill__action:hover { text-decoration: none; }
.tv-auto-pill__close {
  background: transparent;
  border: 0;
  color: rgba(226, 232, 240, 0.6);
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  padding: 0 2px;
  margin-left: 2px;
}
.tv-auto-pill__close:hover { color: rgba(255, 255, 255, 0.95); }
</style>
